namespace Dashboard {

    import PaymentOption = Core.PaymentOption;
    import SavedCardOption = Core.SavedCardOption;
    import PaymentModel = Core.PaymentModel;

    export interface AddOptionConfig {
        completeUrl: string;
        optionsType: string;
        availableOptionsBinding: string;
        selectedOptionsBinding: string;
        selectedOptionsRequest: string;
        analyticsTag: string;
        pageUrl: string;
    }

    interface AddOptionsToBookingResponse {
        AcsRedirectRequired: boolean;
        AcsUrl: string | null;
        AllowThreeDSecureBypass: boolean;
        Amount: number;
        CardFeeAmount: number;
        CurrencyCode: string | null;
        MD: string | null;
        PaReq: string | null;
        PaymentId: number;
        PaymentMessage: string | null;
        PostPayment: boolean;
        ReasonCode: string | null;
        Success: boolean;
    }

    interface AddOptionsRequest {
        BookingId: number;
        AmendBookingOptionsId: string;
        PaymentRequest: {
            BookingId: number;
            DomainId: number;
            CurrencyCode: string,
            Amount: number;
            CultureCode: string;
            PayPalCallbackUrl: string;
            PayPalCancellationUrl: string;
        };
        SelectedOptions?: {
            ChosenQuantity: number;
            OptionId: string;
        }[];
        SelectedSeats?: {
            SeatNumber: string;
            PassengerID: number;
            LegId: string;
            SegmentHash: number;
        };
    }

    export abstract class AddOption {
        protected bookingId : number;
        protected threedsRedirecting : boolean;
        protected options;
        protected selectedOptions;
        protected ThreeDSecure;
        protected amendBookingOptionsId : string;
        protected handBaggageInformation;
        protected paymentTab : number;
        protected paymentMethodText: string[] = ['New Card', 'PayPal', 'Saved Card'];
        protected supplierIds: number[];

        protected dataLoaded: boolean;
        protected selectedOptionsTotalPrice: number = 0;
        protected isPartOfHoliday: boolean;
        protected paymentOptions: PaymentOption[];
        protected paymentModel: PaymentModel;
        protected savedCards: SavedCardOption[];

        constructor(
            protected $location: ng.ILocationService,
            protected $q: ng.IQService,
            protected $routeParams,
            protected $scope: ng.IScope,
            protected $window, // unable to assign to $window.location if its type is declared
            protected broker: Core.Broker,
            protected customCurrencyFilter,
            protected dashboardBookingService,
            protected flightExtrasService,
            protected mybTrack,
            protected paymentDetailsService,
            protected paymentsService,
            protected paymentsUtilService: Core.PaymentsUtilService,
            protected resourceService: Core.ResourceService,
            protected rootPath: Core.RootPath,
            protected urlUtils,
            protected dateUtil,
            protected currentUser
        ) {
            const flightBookingType = 1;
            this.bookingId = parseInt(this.$routeParams.bookingId, 10);

            const getOptions = this.paymentDetailsService.getPaymentOptions(flightBookingType,
                this.bookingId,
                TRPCONFIG.Domain.CurrencyCode);
            const getCards = this.paymentDetailsService.getSavedPaymentCards(flightBookingType,
                this.bookingId,
                TRPCONFIG.Domain.CurrencyCode);

            this.$q.all([getOptions, getCards])
                .then(results => {
                        this.paymentOptions = this.paymentsUtilService.convertMybPaymentOptions(results[0]);

                        if (results[1]) {
                            this.savedCards = this.paymentsUtilService.convertMybSavedCards(results[1], results[0]);
                        }
                    }
                );

            this.paymentModel = this.paymentsUtilService.createEmptyPaymentModel();

            this.flightExtrasService.getOptionsData(this.bookingId).then((optionsData) => {
                if (!optionsData || !this.canAddOption(optionsData) || !optionsData[this.getConfig().optionsType]) {
                    $location.path(`dashboard/bookings/reference/${this.bookingId}`);
                    return;
                }

                this[this.getConfig().availableOptionsBinding] = optionsData[this.getConfig().optionsType];
                this.amendBookingOptionsId = optionsData.AmendBookingOptionsId;
                this.handBaggageInformation = optionsData.HandBaggageInformation;
                if (this.$routeParams.ppResp) {
                    this.showError(this.resourceService.get(this.paymentsService.getPayPalResponseMessage(
                        this.$routeParams.ppResp)));
                }
            }, () => this.$location.path(`dashboard/bookings/reference/${this.bookingId}`));

            this.$scope.$on('3DSValidationComplete', (ev, data) => {
                this.finaliseOptions(data);
            });

            this.dashboardBookingService.getFlightBooking(this.$routeParams.bookingId).then(r => {
                this.supplierIds = r.data.SupplierIds;
                this.mybTrack.track(this.getConfig().analyticsTag, {
                    Source: this.$routeParams.source === 'noneml' ? 'email' : 'cross-Sell',
                    SupplierId: this.supplierIds,
                    BookingId: this.bookingId
                });
            });

            this.dashboardBookingService.isPartOfHoliday(this.$routeParams.bookingId)
                .then(r => this.isPartOfHoliday = r);

            this.dataLoaded = true;
        }

        private generateTokens() {
            return this.paymentsService.generateTokens(
                this.paymentModel.cardDetail.expiryDate.month,
                this.paymentModel.cardDetail.expiryDate.year,
                this.paymentModel.saveCard
            );
        }

        protected abstract analyticsTrackOpenOption();
        protected abstract getConfig() : AddOptionConfig;
        protected abstract canAddOption(optionsData) : boolean;

        private makePayment() {
            this.analyticsTrackOpenOption();

            const expires = this.paymentModel.cardDetail.expiryDate;
            const today = this.dateUtil.todayPlus();
            if (expires &&
                expires.year
                && expires.month
                && expires.year <= today.getFullYear()
                && expires.month < (today.getMonth() + 1)) {

                this.showError(this.resourceService.get('CARDHASEXPIRED'));
                return;
            }

            this.block(true);

            const requireValidation = this.paymentModel.cardDetail.isSavedCard === false
                && !this.paymentModel.isPayPalPayment;

            this.flightExtrasService.addOptionsToBooking(this.formatClearRequestPayload(), requireValidation)
                .then((result: AddOptionsToBookingResponse) => {
                    if (result.Success) {
                        if (result.AcsRedirectRequired) {
                            if (this.paymentModel.isPayPalPayment) {
                                //Paypal redirection
                                this.$window.location = result.AcsUrl;
                            } else {
                                //3ds redirection
                                this.redirectTo3dSecurePayment(result);
                            }
                        } else {
                            //Show complete page
                            this.$location.path(String.format(this.getConfig().completeUrl, this.bookingId));
                        }
                    } else {
                        if (result.PostPayment) {
                            this.handlePaymentSuccessOptionsError();
                            return;
                        }
                        const message = result.ReasonCode ?
                            this.paymentDetailsService.getcardFailureReasonMessage(result.ReasonCode)
                            : result.PaymentMessage;
                        this.handleFailurePayment(message);
                    }
                }, () => {
                    this.showError(this.resourceService.get('PAYBALANCEERROR'));
                });
        }

        private formatClearRequestPayload(): AddOptionsRequest {
            const root = this.urlUtils.getOrigin() + this.rootPath.getRootPath();
            const payPalCallbackUrl =
                '{0}addoptionspaypalcallback?paymentId={1}&bookingId={2}&ppResp={3}' +
                '&amendBookingOptionsId={4}&pageUrl={5}';

            let request: AddOptionsRequest = {
                BookingId: this.bookingId,
                AmendBookingOptionsId: this.amendBookingOptionsId, //Default, As it is not existing payment.
                PaymentRequest: {
                    BookingId: this.bookingId,
                    DomainId: TRPCONFIG.Domain.DomainId,
                    CurrencyCode: TRPCONFIG.Domain.CurrencyCode,
                    Amount: this.selectedOptionsTotalPrice,
                    CultureCode: TRPCONFIG.Domain.CultureCode,
                    PayPalCallbackUrl: String.format(payPalCallbackUrl, root, '$PaymentId', this.bookingId, 'success',
                        this.amendBookingOptionsId, this.getConfig().pageUrl),
                    PayPalCancellationUrl: String.format(payPalCallbackUrl, root, 0, this.bookingId, 'cancel',
                        this.amendBookingOptionsId, this.getConfig().pageUrl)
                },
                [this.getConfig().selectedOptionsRequest]: this[this.getConfig().selectedOptionsBinding]
            };

            this.paymentsUtilService.addPaymentDetailsToClearRequest(this.paymentModel, request.PaymentRequest);

            return request;
        }

        private finaliseOptions(data): void {
            this.block(true);
            this.flightExtrasService.finaliseOptions(this.formatFinaliseOptionsRequestPayload(data))
                .then((result: AddOptionsToBookingResponse) => {
                    this.$scope.$broadcast('3DSValidationFinished', result.Success);

                    if (result.Success) {
                        this.$location.path(String.format(this.getConfig().completeUrl, this.bookingId));
                    } else {
                        if (result.PostPayment) {
                            this.handlePaymentSuccessOptionsError();
                            return;
                        }
                        this.handleFailurePayment(result.PaymentMessage);
                    }
                }, () => {
                    this.showError(this.resourceService.get('AUTHORISEEERROR'));
                });

            this.$scope.$apply(() => {
                this.threedsRedirecting = false;
            });
        }

        private formatFinaliseOptionsRequestPayload(data): Core.FinaliseOptionsRequest {
            return <Core.FinaliseOptionsRequest> {
                BookingId: this.bookingId,
                AmendBookingOptionsId: this.amendBookingOptionsId,
                PaymentId: data.prePaymentId,
                PaRes: decodeURIComponent(data.paRes),
                MD: data.md,
                DomainId: TRPCONFIG.Domain.DomainId,
                CultureCode: TRPCONFIG.Domain.CultureCode
            };
        }

        private showError(msg): void {
            this.$scope.$broadcast('flash:error', msg);
        }

        private block(completing) {
            this.$scope.$emit('block', {
                title: this.resourceService.get(completing ? 'COMPLETINGPAYMENT' : 'PREPARINGPAYMENT'),
                loadingScreenStyle: 'default-addBaggage'
            });
        }

        private redirectTo3dSecurePayment(result): void {
            const root = this.urlUtils.getOrigin() + this.rootPath.getRootPath();
            //Create threeDSecure object
            this.ThreeDSecure = {
                PaReq: result.PaReq,
                MD: result.MD,
                TermUrl: String.format('{0}threedsecure?prePaymentID={1}', root, result.PaymentId),
                AcsUrl: result.AcsUrl,
                CanByPass: result.AllowThreeDSecureBypass,
                PrePaymentID: result.PaymentId
            };
            this.$scope.$broadcast('request3DSValidation', this.ThreeDSecure, {
                Journey: 'AddOptions',
                BookingID: this.bookingId,
                SignedIn: this.currentUser.get().SignedIn
            });
            this.threedsRedirecting = true;
            this.broker.publish('unblock');
            this.$scope.$emit('unblock');
        }

        private handleFailurePayment(msg): void {
            const messages = [this.resourceService.get('PAYMENTFAILED') + msg,
                String.format(this.resourceService.get('PAYMENTASSISTENCE'),
                    this.resourceService.get('TELEPHONENUMBER'))];
            this.showErrorMessages(messages);
        }

        private handlePaymentSuccessOptionsError(): void {
            const message = String.format(this.resourceService.get('AMENDMENTERRORMSG'),
                this.resourceService.get('TELEPHONENUMBER'));
            this.showErrorMessages(message);
        }

        private showErrorMessages(messages): void {
            this.showError(messages);
            this.broker.publish('unblock');
            this.$scope.$emit('unblock');
        }
    }
}
